import { HighlightedFields, PlaceDetails } from '@/map/domain/entities/map.types';
import { useEffect, useState, useRef } from 'react';

import { FormikContextType } from 'formik';
import { GoogleMapsService } from '@/map/application/services/googleMapsService';
import { LocationFormData } from '../validationSchema';

export const useLocationFormMap = (formik: FormikContextType<LocationFormData>) => {
  const [markerPosition, setMarkerPosition] = useState<google.maps.LatLngLiteral | null>(null);
  const [selectedPlaceDetails, setSelectedPlaceDetails] = useState<PlaceDetails | null>(null);
  const [selectedPlace, setSelectedPlace] = useState<google.maps.places.Place | null>(null);
  const [highlightedFields, setHighlightedFields] = useState<HighlightedFields>({});
  const previousValuesRef = useRef<LocationFormData | null>(null);

  // Initialize state from Formik values on mount or when values change significantly
  useEffect(() => {
    const { latitude, longitude, address, city, state, zipCode, country } = formik.values;
    const previousValues = previousValuesRef.current;

    // Check if we have location data to initialize
    const hasCoordinates = latitude != null && longitude != null;
    const hasAddressData = address || city || state || zipCode;

    // Check if values have changed significantly (initial load or major change)
    const valuesChanged =
      !previousValues ||
      previousValues.latitude !== latitude ||
      previousValues.longitude !== longitude ||
      previousValues.address !== address ||
      previousValues.city !== city ||
      previousValues.state !== state ||
      previousValues.zipCode !== zipCode ||
      previousValues.country !== country;

    // Only update if we have data and values changed
    if ((hasCoordinates || hasAddressData) && valuesChanged) {
      // Initialize marker position if coordinates exist
      if (hasCoordinates) {
        setMarkerPosition({
          lat: latitude,
          lng: longitude
        });
      }

      // Initialize place details if address data exists
      if (hasAddressData || hasCoordinates) {
        // Truncate values to match field limits
        const truncatedState = state ? state.substring(0, 3) : '';

        const initialPlaceDetails: PlaceDetails = {
          address: address ?? '',
          city: city ?? '',
          state: truncatedState,
          zipCode: zipCode ?? '',
          country: country ?? '',
          latitude: latitude,
          longitude: longitude
        };
        setSelectedPlaceDetails(initialPlaceDetails);
      }

      // Update ref to track previous values
      previousValuesRef.current = { ...formik.values };
    }
  }, [formik.values]);

  // Reset field highlights after 3 seconds
  useEffect(() => {
    if (Object.keys(highlightedFields).length > 0) {
      const timer = setTimeout(() => {
        setHighlightedFields({});
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [highlightedFields]);

  const updateFormValues = async (details: PlaceDetails, shouldHighlight = true) => {
    const changedFields: Record<string, boolean> = {};

    // Truncate values to match field limits
    const truncatedState = details.state ? details.state.substring(0, 3) : '';
    const truncatedZipCode = details.zipCode ? details.zipCode.substring(0, 10) : '';

    // Check which fields have changed
    if (details.address !== selectedPlaceDetails?.address) {
      changedFields.address = true;
      formik.setFieldValue('address', details.address ?? '');
    }
    if (details.city !== selectedPlaceDetails?.city) {
      changedFields.city = true;
      formik.setFieldValue('city', details.city ?? '');
    }
    if (truncatedState !== selectedPlaceDetails?.state) {
      changedFields.state = true;
      formik.setFieldValue('state', truncatedState);
    }
    if (truncatedZipCode !== selectedPlaceDetails?.zipCode) {
      changedFields.zipCode = true;
      formik.setFieldValue('zipCode', truncatedZipCode);
    }
    if (details.country !== selectedPlaceDetails?.country) {
      changedFields.country = true;
      formik.setFieldValue('country', details.country ?? '');
    }
    if (details.latitude !== selectedPlaceDetails?.latitude) {
      changedFields.latitude = true;
      formik.setFieldValue('latitude', details.latitude);
    }
    if (details.longitude !== selectedPlaceDetails?.longitude) {
      changedFields.longitude = true;
      formik.setFieldValue('longitude', details.longitude);
    }

    // Update local state with truncated values
    setSelectedPlaceDetails({
      ...details,
      state: truncatedState,
      zipCode: truncatedZipCode
    });

    // Update ref to prevent useEffect from re-running unnecessarily
    previousValuesRef.current = {
      address: details.address ?? '',
      city: details.city ?? '',
      state: truncatedState,
      zipCode: truncatedZipCode,
      country: details.country ?? '',
      latitude: details.latitude,
      longitude: details.longitude
    };

    // Highlight only changed fields if requested
    if (shouldHighlight && Object.keys(changedFields).length > 0) {
      setHighlightedFields(changedFields);
    }
  };

  const updateLocationAndForm = async (position: google.maps.LatLngLiteral) => {
    setMarkerPosition(position);
    const newLatitude = Number(position.lat.toFixed(6));
    const newLongitude = Number(position.lng.toFixed(6));

    const geocoder = new google.maps.Geocoder();
    try {
      const result = await geocoder.geocode({ location: position });
      if (result.results[0]) {
        const addressComponents = result.results[0].address_components || [];
        const googleMapsService = new GoogleMapsService();
        const details = googleMapsService.extractAddressComponents(addressComponents);
        const newDetails = {
          ...details,
          latitude: newLatitude,
          longitude: newLongitude
        };
        await updateFormValues(newDetails);
      }
    } catch {
      // Handle error silently
    }
  };

  const handlePlaceSelect = async (place: google.maps.places.Place) => {
    try {
      await place.fetchFields({
        fields: ['displayName', 'formattedAddress', 'location', 'addressComponents']
      });

      if (place.location) {
        const newPosition = {
          lat: place.location.lat(),
          lng: place.location.lng()
        };
        setSelectedPlace(place);
        await updateLocationAndForm(newPosition);
      }
    } catch {
      // Handle error silently
    }
  };

  const handleMarkerDragEnd = async (position: google.maps.LatLngLiteral) => {
    await updateLocationAndForm(position);
  };

  return {
    markerPosition,
    selectedPlaceDetails,
    setSelectedPlaceDetails,
    handlePlaceSelect,
    handleMarkerDragEnd,
    setMarkerPosition,
    highlightedFields,
    updateFormValues,
    selectedPlace
  };
};
